import React, { useEffect } from 'react';
import { createConsumer, Subscription } from '@rails/actioncable';
import { Game, Player, Stack, StackEntry } from '../types/models';

// Create Action Cable consumer
const consumer = createConsumer(import.meta.env.VITE_WS_URL || 'ws://localhost:3003/cable');

export interface GameSubscriptionCallbacks {
  onConnected?: (info: { reconnected: boolean }) => void;
  onRejected?: () => void;
  onGameUpdate?: (game: Game) => void;
  onPlayerJoin?: (player: Player) => void;
  onPlayerUpdate?: (player: Player) => void;
  onPlayerRemoved?: (player: Player) => void;
  onStackCreate?: (stack: Stack) => void;
  onStackUpdate?: (stack: Stack) => void;
  onStackEntryCreate?: (entry: StackEntry) => void;
}

// Fix Active Storage URLs to use the correct host in development
const fixActiveStorageUrl = (url: string | null) => {
  if (!url) return url;
  
  // In production, URLs should already be correct
  if (import.meta.env.PROD) {
    return url;
  }
  
  // In development, fix port issues
  try {
    const urlObj = new URL(url);
    if (urlObj.pathname.includes('/rails/active_storage')) {
      // Replace the port with 3003 (Rails server port in dev)
      urlObj.port = '3003';
      return urlObj.toString();
    }
  } catch (e) {
    console.warn('Failed to parse URL:', url);
  }
  return url;
};

// Fix player data from WebSocket messages
const fixPlayerData = (player: any) => {
  return {
    ...player,
    avatarUrl: fixActiveStorageUrl(player.avatarUrl)
  };
};

export class GameChannel {
  private subscription: Subscription | null = null;
  private hasConnected = false;

  constructor(private gameKey: string, private callbacks: GameSubscriptionCallbacks = {}) {
    console.log('GameChannel: Creating channel for game:', gameKey);
  }

  subscribe(): void {
    console.log('GameChannel: Attempting to subscribe to game:', this.gameKey);
    this.subscription = consumer.subscriptions.create(
      {
        channel: 'GameChannel',
        game_key: this.gameKey,
      },
      {
        connected: () => {
          console.log('GameChannel: Successfully connected to game:', this.gameKey);
          const reconnected = this.hasConnected;
          this.hasConnected = true;
          this.callbacks.onConnected?.({ reconnected });
        },
        disconnected: () => {
          console.log('GameChannel: Disconnected from game:', this.gameKey);
        },
        rejected: () => {
          console.error('GameChannel: Connection rejected for game:', this.gameKey);
          this.callbacks.onRejected?.();
        },
        received: (data: {
          type: string;
          payload: Game | Player | Stack | StackEntry;
        }) => {
          console.log('GameChannel: Received message:', data);
          switch (data.type) {
            case 'game_update':
              const game = data.payload as Game;
              game.players = game.players?.map(fixPlayerData) || [];
              this.callbacks.onGameUpdate?.(game);
              break;
            case 'player_join':
              this.callbacks.onPlayerJoin?.(fixPlayerData(data.payload as Player));
              break;
            case 'player_update':
              this.callbacks.onPlayerUpdate?.(fixPlayerData(data.payload as Player));
              break;
            case 'player_removed':
              this.callbacks.onPlayerRemoved?.(fixPlayerData(data.payload as Player));
              break;
            case 'stack_create':
              this.callbacks.onStackCreate?.(data.payload as Stack);
              break;
            case 'stack_update':
              this.callbacks.onStackUpdate?.(data.payload as Stack);
              break;
            case 'stack_entry_create':
              this.callbacks.onStackEntryCreate?.(data.payload as StackEntry);
              break;
          }
        },
      }
    );
  }

  unsubscribe(): void {
    if (this.subscription) {
      console.log('GameChannel: Unsubscribing from game:', this.gameKey);
      consumer.subscriptions.remove(this.subscription);
      this.subscription = null;
    }
  }
}

// Custom hook for React components
export function useGameChannel(gameKey: string, callbacks: GameSubscriptionCallbacks) {
  // Use a ref to hold the latest callbacks
  const callbacksRef = React.useRef(callbacks);

  // Update the ref whenever callbacks change
  useEffect(() => {
    callbacksRef.current = callbacks;
  }, [callbacks]);

  useEffect(() => {
    if (!gameKey) return; // Don't subscribe if no key

    console.log('useGameChannel: Setting up channel for game:', gameKey);
    
    // Pass a stable object with functions that read from the ref
    const stableCallbacks: GameSubscriptionCallbacks = {
      onConnected: (info) => callbacksRef.current.onConnected?.(info),
      onRejected: () => callbacksRef.current.onRejected?.(),
      onGameUpdate: (game) => callbacksRef.current.onGameUpdate?.(game),
      onPlayerJoin: (player) => callbacksRef.current.onPlayerJoin?.(player),
      onPlayerUpdate: (player) => callbacksRef.current.onPlayerUpdate?.(player),
      onPlayerRemoved: (player) => callbacksRef.current.onPlayerRemoved?.(player),
      onStackCreate: (stack) => callbacksRef.current.onStackCreate?.(stack),
      onStackUpdate: (stack) => callbacksRef.current.onStackUpdate?.(stack),
      onStackEntryCreate: (entry) => callbacksRef.current.onStackEntryCreate?.(entry),
    };

    const channel = new GameChannel(gameKey, stableCallbacks);
    channel.subscribe();

    return () => {
      console.log('useGameChannel: Cleaning up channel for game:', gameKey);
      channel.unsubscribe();
    };
    // Effect depends only on gameKey to manage subscription lifecycle
  }, [gameKey]); 
}

export default GameChannel; 